import { FormatedReportType } from '@views/pages/Reporting/Reports/types.ts';
import { paths } from '@types';
import { axiosInstance } from '@config/axios/axios.config.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DownloadsTableReportsType } from '@views/pages/Downloads/hooks';
import { createObjectUrlForDownloadItem } from '@util/createObjectUrlForDownloadItem.ts';
import dayjs from 'dayjs';
import { AxiosResponse } from 'axios';
import { isNil } from 'lodash';
import { cleanAnd } from '@common/cache';

const generatePatientReportRequest = async ({
  report_id,
  report_type,
  props,
}: {
  report_id: string;
  report_type: FormatedReportType;
  props: paths['/reporting/patients/reports/{report_id}/csv/request']['post']['requestBody']['content']['application/json'];
}) => {
  const formattedAnd = cleanAnd(props);
  const response = axiosInstance.post<
    paths['/reporting/patients/reports/{report_id}/csv/request']['post']['requestBody']['content']['application/json'],
    AxiosResponse<
      paths['/reporting/patients/reports/{report_id}/csv/request']['post']['responses']['201']['content']['application/json']
    >
  >(`/reporting/${report_type}/reports/${report_id}/csv/request`, {
    ...props,
    ...(props.options && { formattedAnd }),
  });
  const data = await response;

  return data;
};

export const useGeneratePatientReportMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['generateReport'],
    mutationFn: ({
      report_id,
      report_type,
      props,
    }: {
      report_id: string;
      report_type: FormatedReportType;
      props: paths['/reporting/patients/reports/{report_id}/csv/request']['post']['requestBody']['content']['application/json'];
    }) =>
      generatePatientReportRequest({
        report_id,
        report_type,
        props,
      }),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ['reportDownloadList'] }),
  });

  return mutation;
};

// ---------- DOWNLOAD REPORT CSV ----------

interface IFileDownloadProps {
  id: string;
  report_name: string;
  report_type: DownloadsTableReportsType;
  timestamp: string | null;
}

export const reportTableCsvDownloadRequest = async ({
  id,
  report_name,
  report_type,
  timestamp,
}: IFileDownloadProps) => {
  const response = await axiosInstance.get(`/reporting/downloads/${id}/csv`, {
    headers: {
      'Content-type': 'application/xml',
    },
    responseType: 'blob',
  });

  const data = (await response).data;

  createObjectUrlForDownloadItem(
    data,
    `${report_name}_${report_type}${!isNil(timestamp) ? '_' + dayjs(timestamp).format('YYYY-MM-DD hh:mm A') : ''}`,
  );

  return data;
};

export const useTableDownloadReportMutation = () => {
  const mutation = useMutation({
    mutationKey: ['reportDownloadTableCSV'],
    mutationFn: (entry: IFileDownloadProps) =>
      reportTableCsvDownloadRequest(entry),
  });

  return mutation;
};

// ---------- DELETE TABLE REPORT ----------

const deleteDownloadReportTableListRequest = async (list: string[]) => {
  const response = axiosInstance.delete<{
    list: string[];
  }>(`/reporting/downloads`, {
    params: {
      report_download_ids: list,
    },
  });
  const data = await response;

  return data;
};

export const useDeleteReportDownloadTableMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['reportDeleteDownloadTableCSV'],
    mutationFn: (list: string[]) => deleteDownloadReportTableListRequest(list),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ['reportDownloadList'] }),
  });

  return mutation;
};

////////// CANCEL REPORT GENERATING

const cancelReportGeneratingRequest = async (report_download_id: string) => {
  const response = axiosInstance.post<{
    report_download_id: string;
  }>(`/reporting/downloads/${report_download_id}/cancel`);
  const data = await response;

  return data;
};

export const useCancelReportGeneratingMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['cancelReportGenerating'],
    mutationFn: (report_download_id: string) =>
      cancelReportGeneratingRequest(report_download_id),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ['reportDownloadList'] }),
  });

  return mutation;
};

////////// RETRY REPORT GENERATING

const retryReportGeneratingRequest = async (report_download_id: string) => {
  const response = axiosInstance.post<{
    report_download_id: string;
  }>(`/reporting/downloads/${report_download_id}/retry`);
  const data = await response;

  return data;
};

export const useRetryReportGeneratingMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['cancelReportGenerating'],
    mutationFn: (report_download_id: string) =>
      retryReportGeneratingRequest(report_download_id),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ['reportDownloadList'] }),
  });

  return mutation;
};

////// GENERATE CARE PLAN FILE ///////////

const generateCarePlanRequest = async ({
  props,
}: {
  props: paths['/patient/care-plans/downloads']['post']['requestBody']['content']['application/json'];
}) => {
  const response = axiosInstance.post<
    paths['/patient/care-plans/downloads']['post']['requestBody']['content']['application/json'],
    AxiosResponse<
      paths['/patient/care-plans/downloads']['post']['responses']['200']['content']['application/json']
    >
  >(`/patient/care-plans/downloads`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useGenerateCarePlanMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['generateCarePlan'],
    mutationFn: ({
      props,
    }: {
      props: paths['/patient/care-plans/downloads']['post']['requestBody']['content']['application/json'];
    }) =>
      generateCarePlanRequest({
        props,
      }),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ['carePlanDownloadList'] }),
  });

  return mutation;
};

const cancelCarePlanGeneratingRequest = async (id: string) => {
  const response = axiosInstance.post<{
    report_download_id: string;
  }>(`/patient/care-plans/downloads/${id}/cancel`);
  const data = await response;

  return data;
};

export const useCancelCarePlanGeneratingMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['cancelCarePlanGenerating'],
    mutationFn: (id: string) => cancelCarePlanGeneratingRequest(id),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ['carePlanDownloadList'] }),
  });

  return mutation;
};

const deleteDownloadCarePlanTableListRequest = async (list: string[]) => {
  const response = axiosInstance.delete<{
    list: string[];
  }>(`/patient/care-plans/downloads`, {
    params: {
      care_plan_download_ids: list,
    },
  });
  const data = await response;

  return data;
};

export const useDeleteCarePlanDownloadTableMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['carePlanDeleteDownloadTableCSV'],
    mutationFn: (list: string[]) =>
      deleteDownloadCarePlanTableListRequest(list),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ['carePlanDownloadList'] }),
  });

  return mutation;
};

export const carePlanTableDownloadRequest = async ({
  id,
  name,
  timestamp,
}: {
  id: string;
  name: string;
  timestamp: string | null;
}) => {
  const response = await axiosInstance.get(
    `/patient/care-plans/downloads/${id}/file`,
    {
      headers: {
        'Content-type': 'application/zip',
      },
      responseType: 'blob',
    },
  );

  const data = (await response).data;

  createObjectUrlForDownloadItem(
    data,
    `${name}_${!isNil(timestamp) ? '_' + dayjs(timestamp).format('YYYY-MM-DD hh:mm A') : ''}`,
    false,
  );

  return data;
};

export const useCarePlanDownloadMutation = () => {
  const mutation = useMutation({
    mutationKey: ['carePlanDownloadTableCSV'],
    mutationFn: (entry: {
      id: string;
      name: string;
      timestamp: string | null;
    }) => carePlanTableDownloadRequest(entry),
  });

  return mutation;
};

const retryCarePlanGeneratingRequest = async (id: string) => {
  const response = axiosInstance.post<{
    id: string;
  }>(`/patient/care-plans/downloads/${id}/retry`);
  const data = await response;

  return data;
};

export const useRetryCarePlanGeneratingMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['retryCarePlanGenerating'],
    mutationFn: (id: string) => retryCarePlanGeneratingRequest(id),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ['carePlanDownloadList'] }),
  });

  return mutation;
};
